import {AfterViewInit, Component, HostListener, NgZone, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {StatusBar, Style} from "@capacitor/status-bar";
import {Capacitor, PluginListenerHandle} from "@capacitor/core";
import {GoogleAnalyticsService} from "./shared/services/google-analytics/google-analytics.service";
import {DeviceService} from "./shared/services/device/device.service";
import {MobileService} from "./shared/services/mobile/mobile.service";
import {RouteListenerService} from "./shared/services/route-listener/route-listener.service";
import {CustomIconsService} from "./shared/services/custom-icons/custom-icons.service";
import {environment} from "../environments/environment";
import SafeArea from "./shared/interfaces/safe-area-plugin.interface";
import {PwaService} from "./shared/services/pwa/pwa.service";
// import {PushService} from "./shared/services/push/push.service";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'check';
  checkInnerHeightDebounce: Subject<any> = new Subject();
  innerHeight: number;
  private safeAreaListener: PluginListenerHandle | null = null;

  constructor(
    public router: Router,
    public googleAnalyticsService: GoogleAnalyticsService,
    private zone: NgZone,
    private deviceService: DeviceService,
    private mobileService: MobileService,
    private routeListenerService: RouteListenerService,
    private pwaService: PwaService,
    private customIconsService: CustomIconsService
    // private pushService: PushService,
  ) {
    this.customIconsService.registerIcons();

    if(this.googleAnalyticsService.canAddGTM()) {
      this.googleAnalyticsService.pushTagForPageLinks();
    }

    this.checkInnerHeightDebounce.pipe(debounceTime(250)).subscribe(() => {
      this.checkInnerHeight();
    });
  }

  async ngOnInit() {
    if(this.deviceService.isMobileDevice()) {
      await StatusBar.setStyle({style: Style.Light});
    }
    // On Android with edge-to-edge, env(safe-area-inset-bottom) is always 0 — the plugin
    // reads the real navigation bar height and exposes it via --safe-area-bottom.
    // iOS is handled natively by env(safe-area-inset-bottom) without needing a plugin.
    if (Capacitor.getPlatform() === 'android') {
      this.safeAreaListener = await SafeArea.addListener('safeAreaChanged', ({ bottom }) => {
        document.documentElement.style.setProperty('--safe-area-bottom', `${bottom}px`);
      });
    }
    this.deviceService.saveDeviceInfoToLS();
    // await this.pushService.registerNotifications();
    await this.mobileService.openUpdateDialogIfNotUpdated();
    await this.mobileService.openAppDownloadDialogIfNeeded();

    this.routeListenerService.subscribeForWebNotificationId();

    // Hide Firebase emulator banner if configured
    if (environment.firebase?.['hideFirebaseEmulatorBanner']) {
      document.body.classList.add('hide-firebase-emulator-banner');
    }
  }

  ngOnDestroy(): void {
    this.safeAreaListener?.remove();
  }

  ngAfterViewInit(): void {
    this.checkInnerHeightDebounce.next(null);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.checkInnerHeightDebounce.next(null);
  }

  checkInnerHeight() {
    let innerHeight = window.innerHeight;
    if (this.innerHeight !== innerHeight) {
      this.innerHeight = innerHeight;
      document.documentElement.style.setProperty('--vh', `${innerHeight}px`);
    }
  }
}
